import React, { useMemo, useState } from 'react';
import { Search, Download, RefreshCw, ChevronLeft, ChevronRight, MapPin, Eye, Calendar, Clock, Truck, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { mockUnifiedOrders } from '../../utils/mockData';
import { OrderStatus } from '../../types';
export function RefillsHistory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const filteredOrders = useMemo(() => {
    return mockUnifiedOrders.filter(order => {
      // Only show refills (assuming all in mockUnifiedOrders are refills for now,
      // or we can filter by a property if available. Based on blueprint, they are separate modules)
      const query = searchQuery.toLowerCase();
      const matchesSearch = order.orderId.toLowerCase().includes(query) || order.customer.name.toLowerCase().includes(query) || order.erxReference && order.erxReference.toLowerCase().includes(query);
      if (!matchesSearch) return false;
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;
      return true;
    });
  }, [searchQuery, statusFilter]);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);
  const getStatusBadgeVariant = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
        return 'info';
      case 'dispensed':
        return 'success';
      case 'delivered':
        return 'success';
      case 'rejected':
        return 'danger';
      default:
        return 'neutral';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-6 pb-12" data-id="element-662">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-663">
        <div data-id="element-664">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-665">
            Dawaee Refills History
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-666">
            View and manage historical refill requests and their fulfillment
            status
          </p>
        </div>
        <div className="flex gap-3" data-id="element-667">
          <Button variant="outline" data-id="element-668">
            <RefreshCw className="w-4 h-4 mr-2" data-id="element-669" />
            Refresh
          </Button>
          <Button variant="outline" data-id="element-670">
            <Download className="w-4 h-4 mr-2" data-id="element-671" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card data-id="element-672">
        <CardContent className="pt-6" data-id="element-673">
          <div className="flex flex-col lg:flex-row gap-4" data-id="element-674">
            <div className="relative flex-1" data-id="element-675">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-676" />
              <Input placeholder="Search by Order ID, eRx, or Patient..." value={searchQuery} onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }} className="pl-9" data-id="element-677" />
            </div>
            <Select value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} options={[{
            value: 'all',
            label: 'All Statuses'
          }, {
            value: 'pending',
            label: 'Pending'
          }, {
            value: 'dispensed',
            label: 'Dispensed'
          }, {
            value: 'delivered',
            label: 'Delivered'
          }, {
            value: 'rejected',
            label: 'Cancelled'
          }]} className="w-full lg:w-48" data-id="element-678" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-679">
        <CardContent className="p-0" data-id="element-680">
          <div className="overflow-x-auto" data-id="element-681">
            <Table data-id="element-682">
              <TableHeader data-id="element-683">
                <TableRow data-id="element-684">
                  <TableHead data-id="element-685">Order Details</TableHead>
                  <TableHead data-id="element-686">Patient</TableHead>
                  <TableHead data-id="element-687">Fulfillment</TableHead>
                  <TableHead data-id="element-688">Status</TableHead>
                  <TableHead data-id="element-689">Date & Time</TableHead>
                  <TableHead className="text-right" data-id="element-690">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-691">
                {paginatedOrders.map(order => <TableRow key={order.id} className="hover:bg-slate-50" data-id="element-692">
                    <TableCell data-id="element-693">
                      <div className="font-medium text-slate-900" data-id="element-694">
                        {order.orderId}
                      </div>
                      {order.erxReference && <div className="text-xs text-teal-600 font-mono mt-0.5" data-id="element-695">
                          {order.erxReference}
                        </div>}
                    </TableCell>
                    <TableCell data-id="element-696">
                      <div className="font-medium text-slate-900" data-id="element-697">
                        {order.customer.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-698">
                        {order.customer.phone}
                      </div>
                    </TableCell>
                    <TableCell data-id="element-699">
                      <div className="flex flex-col gap-1" data-id="element-700">
                        <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-701">
                          <MapPin className="w-3 h-3 text-slate-400" data-id="element-702" />
                          {order.branch}
                        </div>
                        <Badge variant={!order.isPickup ? 'info' : 'neutral'} size="sm" className="w-fit" data-id="element-703">
                          {!order.isPickup ? 'Home Delivery' : 'Store Pickup'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-704">
                      <Badge variant={getStatusBadgeVariant(order.status)} size="sm" className="capitalize" data-id="element-705">
                        {order.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-706">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-707">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" data-id="element-708" />
                        {new Date(order.receivedAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1" data-id="element-709">
                        <Clock className="w-3.5 h-3.5 text-slate-400" data-id="element-710" />
                        {new Date(order.receivedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" data-id="element-711">
                      <Button variant="ghost" size="sm" data-id="element-712">
                        <Eye className="w-4 h-4 mr-2" data-id="element-713" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {filteredOrders.length > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100" data-id="element-714">
              <p className="text-sm text-slate-500" data-id="element-715">
                Showing{' '}
                <span className="font-medium text-slate-700" data-id="element-716">
                  {startIndex + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium text-slate-700" data-id="element-717">
                  {Math.min(startIndex + itemsPerPage, filteredOrders.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium text-slate-700" data-id="element-718">
                  {filteredOrders.length}
                </span>{' '}
                orders
              </p>
              <div className="flex items-center gap-2" data-id="element-719">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-720">
                  <ChevronLeft className="w-4 h-4 mr-1" data-id="element-721" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-id="element-722">
                  Next <ChevronRight className="w-4 h-4 ml-1" data-id="element-723" />
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>
    </motion.div>;
}